import { useState } from "react";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Loader2, Languages, X, AlertTriangle } from "lucide-react";
import { useEntityList } from "./useEntityList";
import { formatApiError } from "@/lib/api";

export default function EntityListShell({
  collection,
  title,
  description,
  newLabel,
  testidBase,
  emptyForm,
  toFormState,
  buildPayload,
  renderRowSummary,
  renderFields,
}) {
  const { items, loading, error, create, update, remove, move } = useEntityList(collection);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSaveError("");
    setFormKey((k) => k + 1);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm(toFormState(item));
    setSaveError("");
    setFormKey((k) => k + 1);
    setModalOpen(true);
  };

  const onSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload = await buildPayload(form);
      if (editingId) await update(editingId, payload);
      else await create(payload);
      setModalOpen(false);
    } catch (e) {
      setSaveError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await remove(deleteId);
      setDeleteId(null);
    } catch (e) {
      setDeleteError(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid={`${testidBase}-list`}>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-[var(--text-0)]">{title}</h2>
          {description && <p className="text-sm text-[var(--text-2)] mt-1 max-w-xl">{description}</p>}
        </div>
        <button onClick={openNew} data-testid={`${testidBase}-add-btn`} className="btn-primary text-xs">
          <Plus size={14} /> {newLabel}
        </button>
      </div>

      {error && <div className="border border-red-500/40 text-red-500 p-3 mb-4 text-sm">{error}</div>}

      {loading ? (
        <div className="text-[var(--text-2)] text-sm py-8 text-center">Laden…</div>
      ) : items.length === 0 ? (
        <div className="border hairline p-8 text-center text-[var(--text-2)] text-sm">Noch keine Einträge.</div>
      ) : (
        <div className="border hairline divide-y divide-[var(--line-soft)]">
          {items.map((item, idx) => (
            <div key={item.id} data-testid={`${testidBase}-row-${idx}`} className="flex items-center gap-4 px-5 py-4">
              <div className="flex flex-col shrink-0">
                <button
                  onClick={() => move(item.id, -1)}
                  disabled={idx === 0}
                  data-testid={`${testidBase}-up-${idx}`}
                  className="w-6 h-6 flex items-center justify-center text-[var(--text-2)] hover:text-[var(--gold)] disabled:opacity-20"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => move(item.id, 1)}
                  disabled={idx === items.length - 1}
                  data-testid={`${testidBase}-down-${idx}`}
                  className="w-6 h-6 flex items-center justify-center text-[var(--text-2)] hover:text-[var(--gold)] disabled:opacity-20"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
              <div className="flex-1 min-w-0">{renderRowSummary(item)}</div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => openEdit(item)}
                  data-testid={`${testidBase}-edit-${idx}`}
                  className="w-9 h-9 flex items-center justify-center border hairline hover:border-[var(--gold)] hover:text-[var(--gold)]"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => setDeleteId(item.id)}
                  data-testid={`${testidBase}-delete-${idx}`}
                  className="w-9 h-9 flex items-center justify-center border hairline hover:border-red-500 hover:text-red-500"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && form && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid={`${testidBase}-modal`}
        >
          <div className="bg-[var(--bg-0)] border hairline max-w-2xl w-full max-h-[88vh] overflow-y-auto p-8 relative">
            <button
              onClick={() => setModalOpen(false)}
              data-testid={`${testidBase}-modal-close`}
              className="absolute right-6 top-6 text-[var(--text-2)] hover:text-[var(--gold)]"
            >
              <X size={18} />
            </button>
            <h3 className="font-display text-xl text-[var(--text-0)] mb-5">
              {editingId ? "Eintrag bearbeiten" : newLabel}
            </h3>
            <div className="flex items-start gap-2 text-xs text-[var(--gold)] bg-[var(--gold)]/5 border border-[var(--gold)]/20 p-3 mb-5">
              <Languages size={14} className="shrink-0 mt-0.5" />
              <span>Nur Deutsch ausfüllen – Englisch &amp; Französisch werden beim Speichern automatisch übersetzt.</span>
            </div>
            <div key={formKey}>{renderFields(form, setForm)}</div>
            {saveError && (
              <div className="text-red-500 text-sm mt-4" data-testid={`${testidBase}-save-error`}>
                {saveError}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setModalOpen(false)} data-testid={`${testidBase}-cancel-btn`} className="btn-ghost text-xs">
                Abbrechen
              </button>
              <button onClick={onSave} disabled={saving} data-testid={`${testidBase}-save-btn`} className="btn-primary text-xs disabled:opacity-50">
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Speichert &amp; übersetzt…
                  </>
                ) : (
                  "Speichern"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid={`${testidBase}-delete-modal`}
        >
          <div className="bg-[var(--bg-0)] border hairline max-w-sm w-full p-8">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle size={18} className="text-red-500" />
              <h3 className="font-display text-lg text-[var(--text-0)]">Eintrag löschen?</h3>
            </div>
            <p className="text-sm text-[var(--text-2)] mb-6">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            {deleteError && <div className="text-red-500 text-sm mb-4">{deleteError}</div>}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteId(null);
                  setDeleteError("");
                }}
                data-testid={`${testidBase}-delete-cancel`}
                className="btn-ghost text-xs"
              >
                Abbrechen
              </button>
              <button
                onClick={onConfirmDelete}
                disabled={deleting}
                data-testid={`${testidBase}-delete-confirm`}
                className="text-xs px-4 py-2 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
              >
                {deleting ? "Löscht…" : "Löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
